import { useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

const useShop = () => {
  const { api, AdminId, shops, setShops } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  // =========================================================
  // DELIVERY CHARGES
  // =========================================================

  const [deliveryCharges, setDeliveryCharges] = useState([]);
  const [deliveryChargesLoading, setDeliveryChargesLoading] = useState(false);

  const [deliveryChargeSaving, setDeliveryChargeSaving] = useState(false);

  const [deliveryChargeDeletingId, setDeliveryChargeDeletingId] =
    useState(null);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  // FETCH SHOPS
  // Original API used Basic token, so use api
  const fetchShops = async (shopId = "") => {
    setLoading(true);

    try {
      const res = await api.post("/api/Shop/v1.0/getShopList", {
        adminId: String(AdminId),
        userId: String(shopId),
      });

      if (res.data?.responseCode === "00") {
        const list = res.data?.data || [];
        setShops(list);
        return list;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch shops");
      }

      setShops([]);
      return [];
    } catch (err) {
      console.error("fetchShops error:", err);
      toast.error("Error fetching shops");
      setShops([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // ADD SHOP
  // Original API used Basic token, so use api
  const addShop = async (payload) => {
    try {
      const res = await api.post("/api/Shop/v1.0/onboardShop", payload);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Shop added");
        await fetchShops();
        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to add shop");
      }

      return false;
    } catch (err) {
      console.error("addShop error:", err);
      toast.error("Error adding shop");
      return false;
    }
  };

  // DELETE SHOP
  // Original API used Bearer token, so use bearerApi
  const deleteShop = async (shopId, onSuccess) => {
    if (!shopId) {
      toast.error("Invalid shop ID");
      return false;
    }

    try {
      const res = await api.post("/api/Shop/v1.0/deleteShopDetails", {
        shopId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Shop deleted");
        await fetchShops();

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to delete shop");
      }

      return false;
    } catch (err) {
      console.error("deleteShop error:", err);
      toast.error("Error deleting shop");
      return false;
    }
  };

  // UPDATE SHOP
  // Original API used Bearer token, so use bearerApi
  const updateShop = async (payload, onSuccess) => {
    try {
      const res = await api.post("/api/Shop/v1.0/updateShopDetails", payload);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Shop updated");
        await fetchShops();

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update shop");
      }

      return false;
    } catch (err) {
      console.error("updateShop error:", err);
      toast.error("Error updating shop");
      return false;
    }
  };

  // FETCH LAT/LONG
  // Original API used Basic token, so use api
  const fetchLatLong = async (address) => {
    if (!address) {
      toast.error("Address is required");
      return {
        latitude: "",
        longitude: "",
      };
    }

    try {
      const res = await api.post("/api/Task/v1.0/getLatLongs", {
        address,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        const d = res.data?.data || {};

        return {
          latitude: d.latitude || d.lat || "",
          longitude: d.longitude || d.lng || "",
        };
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch location");
      }

      return {
        latitude: "",
        longitude: "",
      };
    } catch (err) {
      console.error("fetchLatLong error:", err);
      toast.error("Error fetching location");

      return {
        latitude: "",
        longitude: "",
      };
    }
  };

  // UPDATE SHOP STATUS
  // Original API used Bearer token, so use bearerApi
  const updateShopStatus = async ({ shopId, status, onSuccess }) => {
    if (!shopId) {
      toast.error("Invalid shop ID");
      return false;
    }

    setStatusUpdatingId(shopId);

    try {
      const res = await api.post("/api/Shop/v1.0/updateShopStatus", {
        adminId: String(AdminId),
        shopId: String(shopId),
        status: String(status),
      });

      if (res.data?.responseCode === "00") {
        toast.success(
          status === "1"
            ? "Shop activated successfully"
            : "Shop set offline successfully",
        );

        await fetchShops();

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to update shop status",
        );
      }

      return false;
    } catch (err) {
      console.error("updateShopStatus error:", err);
      toast.error("Error updating shop status");
      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // UPDATE SHOP LAT/LONG
  const updateShopLatLong = async ({
    shopCode,
    latitude,
    longitude,
    onSuccess,
  }) => {
    if (!shopCode) {
      toast.error("Shop code is required");
      return false;
    }

    if (!latitude || !longitude) {
      toast.error("Latitude and longitude are required");
      return false;
    }

    try {
      const res = await api.post("/api/Shop/v1.0/updateShopLatLong", {
        adminId: String(AdminId),
        userId: String(shopCode),
        latitude: String(latitude),
        longitude: String(longitude),
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Shop location updated");
        await fetchShops();

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update location");
      }

      return false;
    } catch (err) {
      console.error("updateShopLatLong error:", err);
      toast.error("Error updating shop location");
      return false;
    }
  };

  // =========================================================
  // GET DELIVERY CHARGES
  // =========================================================

  const fetchDeliveryCharges = async (userId = "") => {
    setDeliveryChargesLoading(true);

    try {
      const res = await api.post("/api/Admin/v1.0/getDeliveryCharges", {
        userid: String(userId || ""),
      });

      if (res.data?.responseCode === "00") {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];

        setDeliveryCharges(list);

        return list;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch delivery charges",
        );
      }

      setDeliveryCharges([]);

      return [];
    } catch (err) {
      console.error("fetchDeliveryCharges error:", err);

      toast.error("Error fetching delivery charges");

      setDeliveryCharges([]);

      return [];
    } finally {
      setDeliveryChargesLoading(false);
    }
  };

  // =========================================================
  // INSERT / UPDATE DELIVERY CHARGE
  // SAME API USED FOR BOTH
  // =========================================================

  const saveDeliveryCharge = async (
    payload,
    { isUpdate = false, onSuccess } = {},
  ) => {
    setDeliveryChargeSaving(true);

    try {
      const requestPayload = {
        adminId: String(AdminId),

        userId: String(payload?.userId ?? ""),

        minimumDistance: String(payload?.minimumDistance ?? ""),

        maximumDistance: String(payload?.maximumDistance ?? ""),

        minimumOrderAmount: String(payload?.minimumOrderAmount ?? ""),

        maximumOrderAmount: String(payload?.maximumOrderAmount ?? ""),

        deliveryCharges: String(payload?.deliveryCharges ?? ""),

        perKmCharges: String(payload?.perKmCharges ?? ""),
      };

      const res = await api.post(
        "/api/Admin/v1.0/insertDeliveryCharges",
        requestPayload,
      );

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage ||
            (isUpdate
              ? "Delivery charge updated successfully"
              : "Delivery charge added successfully"),
        );

        // Fetch all charges again after add/update
        await fetchDeliveryCharges("");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage ||
            (isUpdate
              ? "Failed to update delivery charge"
              : "Failed to add delivery charge"),
        );
      }

      return false;
    } catch (err) {
      console.error("saveDeliveryCharge error:", err);

      toast.error(
        isUpdate
          ? "Error updating delivery charge"
          : "Error adding delivery charge",
      );

      return false;
    } finally {
      setDeliveryChargeSaving(false);
    }
  };

  // =========================================================
  // DELETE DELIVERY CHARGE
  // =========================================================

  const deleteDeliveryCharge = async ({ userId = "", chargeId, onSuccess }) => {
    if (!chargeId) {
      toast.error("Invalid charge ID");
      return false;
    }

    setDeliveryChargeDeletingId(chargeId);

    try {
      const res = await api.post("/api/Admin/v1.0/deleteDeliveryCharges", {
        userid: String(userId || ""),
        chargeId: String(chargeId),
      });

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Delivery charge deleted successfully",
        );

        await fetchDeliveryCharges("");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to delete delivery charge",
        );
      }

      return false;
    } catch (err) {
      console.error("deleteDeliveryCharge error:", err);

      toast.error("Error deleting delivery charge");

      return false;
    } finally {
      setDeliveryChargeDeletingId(null);
    }
  };

  return {
    shops,
    setShops,
    loading,
    fetchShops,
    addShop,
    updateShop,
    deleteShop,
    fetchLatLong,
    updateShopStatus,
    statusUpdatingId,
    updateShopLatLong,


  deliveryCharges,
  setDeliveryCharges,

  deliveryChargesLoading,
  deliveryChargeSaving,
  deliveryChargeDeletingId,

  fetchDeliveryCharges,
  saveDeliveryCharge,
  deleteDeliveryCharge,
  };
};

export default useShop;
